import { useCallback, useEffect, useState } from 'react';
import { ButtonWithIcon, IconButton } from 'src/components/Buttons';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useListingStatusContext } from 'src/contexts/ListingStatusContext';
import { useSearchDataContextWithCatalogFallback } from 'src/contexts/SearchDataContext';
import { vars } from 'src/core/themes';
import { ButtonProps } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import {
  useUserHasAdvancedSearchV2Feature,
  useUserHasFeature,
} from 'src/hooks/useUserHasFeature';
import { SignalIcon } from 'src/svgs';
import { DuplicateOutlinedIcon } from 'src/svgs/DuplicateOutlinedIcon';
import { PendingQCIcon } from 'src/svgs/PendingQC';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getListingBroadcastIcon,
  getListingGroupForListing,
} from 'src/utils/inventoryUtils';
import { getIsPlaceholderEvent } from 'src/utils/placeholderEventsUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  Event,
  Feature,
  Listing,
  ListingClient,
  ListingDetails,
  ListingGroupClient,
  ListingMetrics,
  ListingStatus,
  ListingStatusInfo,
  Marketplace,
  MarketplaceListingStatusInfo,
  QualityControlState,
} from 'src/WebApiController';

export const LaunchBroadcastListing = ({
  forTable,
  listing,
  event,
  variant,
  iconOnlyMode,
  disabled,
  lockListingGroupFromEdits,
}: {
  forTable: boolean;
  listing: Listing;
  event?: Event | null;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
  lockListingGroupFromEdits?: (lock: boolean) => void;
}) => {
  const isMobile = useMatchMedia('mobile');
  const [isLoading, setIsLoading] = useState(false);
  const { showErrorDialog, trackError } = useClientLogContext();
  const { posEntity, updateActivePosEntityInfo } =
    useActivePosEntityContext<ListingDetails>();

  const { activeAccountWebClientConfig } = useAppContext();
  const hasAdvancedSearchV2 = useUserHasAdvancedSearchV2Feature(
    ActionOutboxEntityType.Listing
  );
  const catalogDataContext = useCatalogDataContext();
  const searchFallback = useSearchDataContextWithCatalogFallback();

  // When AdvancedSearchV2 is enabled, use the fallback hook which dispatches
  // to the correct context (SearchDataContext or CatalogDataContext)
  const updateItemInEvent = hasAdvancedSearchV2
    ? searchFallback.updateItemInEvent
    : catalogDataContext.updateItemInEvent;
  const eventsTransformed = hasAdvancedSearchV2
    ? searchFallback.eventsTransformed
    : catalogDataContext.eventsTransformed;
  const refreshExpandedListItems = hasAdvancedSearchV2
    ? () => {
        // v1 calls catalogDataContext.eventsExpansion.refreshExpandedListItems here,
        // which re-fetches listing data from DB (GetListingsSectionalDataForEvents).
        // For v2, we trigger a delayed OS refetch to pick up the broadcast status change.
        // The delay accounts for OS indexing lag after the DB is updated.
        // https://thestubhub.atlassian.net/browse/SAPI-3957
        // https://thestubhub.atlassian.net/browse/SAPI-3892
        setTimeout(() => searchFallback.refetch(), 5_000);
        return Promise.resolve();
      }
    : catalogDataContext.eventsExpansion.refreshExpandedListItems;

  const { refreshMetrics } = useCatalogMetricsContext<ListingMetrics>();

  const hasIgnoreDupFeature = useUserHasFeature(Feature.IgnoreDuplicate);
  const hasMemorizeLastBroadcastListingMarketplaceFeature = useUserHasFeature(
    Feature.MemorizeLastBroadcastListingMarketplace
  );
  const hasReducedBroadcastStatusPullingFrequencyFeature = useUserHasFeature(
    Feature.ReducedBroadcastStatusPullingFrequency
  );

  const isDuplicate =
    !!listing?.dupListingId && (!hasIgnoreDupFeature || !listing?.ignoreDup);

  const isNotListed =
    listing.status !== ListingStatus.Listed &&
    listing.status !== ListingStatus.ListingPending;

  const hasBatchGetListingStatusCallsFeature = useUserHasFeature(
    Feature.BatchGetListingStatusCalls
  );

  const hasPlaceholderEventFeature = useUserHasFeature(
    Feature.PlaceholderEventGroup
  );
  // The backend sets `placeholder` only for synthesized placeholder events,
  // so its presence authoritatively identifies them.
  const isPlaceholderEvent =
    hasPlaceholderEventFeature && event && getIsPlaceholderEvent(event);

  const { getOrSubscribeForListingStatus, isLoading: statusIsLoading } =
    useListingStatusContext();

  useEffect(() => {
    const pollingFrequency = hasBatchGetListingStatusCallsFeature
      ? 2_000
      : hasReducedBroadcastStatusPullingFrequencyFeature
        ? 6_000
        : 2_000;

    // We're only polling in this for the table, because the modal has the MarketplaceBroadcastSection polling already
    if (forTable) {
      // If we have any Marketplace listing pending status - we need to poll to get the latest status
      const statusChecking = setInterval(async () => {
        const needChecking =
          listing.status === ListingStatus.ListingPending ||
          listing.status === ListingStatus.DelistingPending;

        const shouldCheckStatus =
          needChecking &&
          !isLoading &&
          !statusIsLoading &&
          activeAccountWebClientConfig.activeAccountId;

        if (shouldCheckStatus) {
          await tryInvokeApi(
            async () => {
              setIsLoading(true);

              let newListing: ListingStatusInfo | undefined = undefined;
              if (hasBatchGetListingStatusCallsFeature) {
                // When we have batch call feature, we should just skip the per-listing polling
                newListing = getOrSubscribeForListingStatus(listing.id);
              } else {
                const client = new ListingClient(activeAccountWebClientConfig);
                newListing = await client.getListingStatusByListingId(
                  listing.id
                );
              }

              if (newListing) {
                // We we get back a listing from server, it may or may not have finished the job
                // So we just have to assume the status is flowing in one direct only, ie, if we set something to pending,
                // we expect it to either finish or failed (but not reversed)
                let newStatus: ListingStatus | undefined = undefined;
                if (listing.status === ListingStatus.DelistingPending) {
                  // If we are delisting-pending, set if the new status is either Delisted or DelistingFailed
                  if (
                    newListing.status === ListingStatus.Disabled ||
                    newListing.status === ListingStatus.Delisted ||
                    newListing.status === ListingStatus.DelistingFailed
                  ) {
                    newStatus = newListing.status;
                  }
                } else if (listing.status === ListingStatus.ListingPending) {
                  // If we are listing-pending, set if the new status is either Listed, ListedFailed,
                  // or Delisted (OpenSearch data may be stale, showing ListingPending when already Delisted in DB)
                  if (
                    newListing.status === ListingStatus.Disabled ||
                    newListing.status === ListingStatus.Listed ||
                    newListing.status === ListingStatus.ListingFailed ||
                    newListing.status === ListingStatus.Delisted
                  ) {
                    newStatus = newListing.status;
                  }
                }

                let updatedListing = newListing as ListingDetails;

                const newMkpListings = newListing.mkpListings.reduce(
                  (r, c) => {
                    r[c.mkp] = c;
                    return r;
                  },
                  {} as Record<Marketplace, MarketplaceListingStatusInfo>
                );

                updatedListing = {
                  ...(posEntity ?? listing),
                  ...newListing,
                  // for marketplaces, we also need to merge since the MarketplaceListingStatusInfo is just a subset of the MarketplaceListing
                  mkpListings: (posEntity ?? listing).mkpListings?.map(
                    (mkp) => ({
                      ...mkp,
                      ...(newMkpListings[mkp.mkp] ?? {}),
                    })
                  ),
                } as ListingDetails;

                // Only if we do get status changes shall we try to actually update the active-item
                // provider with the latest states, or else the toggle will jump back and forth due
                // to the useEffect earlier (since we modified the active listing)
                if (newStatus) {
                  // Only stop running this if all the status is no longer pending
                  clearInterval(statusChecking);

                  refreshMetrics?.();
                  updateItemInEvent(
                    updatedListing,
                    ActionOutboxEntityType.Listing
                  );
                  if (posEntity && updatedListing.id === posEntity.id) {
                    updateActivePosEntityInfo({
                      posEntity: updatedListing,
                      posEntityId: updatedListing!.id,
                      posEntityDisplayId: updatedListing!.idOnMkp,
                    });
                  }
                }
              }
            },
            (error) => {
              // Because this is a background process - we don't want to show error dialog
              trackError(
                'LaunchBroadcastListing.getListingStatusByListingId',
                error,
                {
                  listingId: listing.id,
                }
              );
            },
            () => setIsLoading(false)
          );
        }
      }, pollingFrequency);

      return () => clearInterval(statusChecking);
    }
  }, [
    activeAccountWebClientConfig,
    listing,
    listing.id,
    listing.idOnMkp,
    refreshMetrics,
    trackError,
    updateItemInEvent,
    forTable,
    posEntity,
    updateActivePosEntityInfo,
    isLoading,
    hasReducedBroadcastStatusPullingFrequencyFeature,
    hasBatchGetListingStatusCallsFeature,
    getOrSubscribeForListingStatus,
    statusIsLoading,
  ]);

  const onBroadcastClick = useCallback(
    async (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Access native event for stopImmediatePropagation if needed
      e.nativeEvent.stopImmediatePropagation?.();
      setIsLoading(true);

      // Update the intention so the UI is changed immediately to the pending state
      const newStatus = isNotListed
        ? ListingStatus.ListingPending
        : ListingStatus.DelistingPending;

      if (forTable) {
        if (listing.ltGrpId) {
          listing.ltGrpActive = newStatus === ListingStatus.ListingPending;

          const listingGroup = getListingGroupForListing(
            event?.viagId,
            listing.ltGrpId,
            eventsTransformed
          );

          if (listingGroup) {
            listingGroup.isLockedFromEdits = true;

            updateItemInEvent(listingGroup, ActionOutboxEntityType.Listing);
          }
        }

        updateItemInEvent(
          { ...listing, status: newStatus },
          ActionOutboxEntityType.Listing
        );
      } else {
        const lastBroadcastedMarketplaces =
          isNotListed && hasMemorizeLastBroadcastListingMarketplaceFeature
            ? await new ListingClient(
                activeAccountWebClientConfig
              ).getLastBroadcastListingMarketplaceByListingId(listing.id)
            : [];

        updateActivePosEntityInfo({
          posEntity: {
            ...posEntity!,
            status: newStatus,
            mkpListings:
              posEntity!.mkpListings?.map((ml) => {
                const keepOriginalStatus =
                  isNotListed &&
                  // Case 1: If user is attempting to broadcast, and marketplace is not enabled - don't turn on the toggle to appear to broadcast
                  (!(ml.isEnabled ?? true) ||
                    // Case 2: Also when user is attempting to broadcast, but marketplace was not broadcasted before - don't turn on the toggle to appear to broadcast
                    (lastBroadcastedMarketplaces.length &&
                      !lastBroadcastedMarketplaces.includes(ml.mkp)));

                return {
                  ...ml,
                  status: keepOriginalStatus ? ml.status : newStatus,
                };
              }) ?? null,
          },
          posEntityId: posEntity!.id,
          posEntityDisplayId: posEntity!.idOnMkp,
        });
      }

      await tryInvokeApi(
        async () => {
          const client = new ListingClient(activeAccountWebClientConfig);

          let result = false;
          if (isNotListed) {
            result = await client.createMarketplaceListings(listing.id, []);
          } else {
            if (listing.ltGrpId) {
              // if this is part of the group, reduce active listing count of the group first
              await new ListingGroupClient(
                activeAccountWebClientConfig
              ).setListingActiveInGroup(listing.ltGrpId, listing.id, false);
            }

            result = await client.deleteMarketplaceListings(listing.id, []);
          }

          return result;
        },
        (error) => {
          showErrorDialog(
            isNotListed
              ? 'ListingClient.createMarketplaceListings'
              : 'ListingClient.deleteMarketplaceListings',
            error,
            {
              trackErrorData: {
                listingId: listing.id,
                idOnMkp: listing.idOnMkp,
              },
            }
          );
        },
        () => {
          setIsLoading(false);
          if (listing.ltGrpId) {
            lockListingGroupFromEdits?.(false);
          }
          refreshExpandedListItems();
        }
      );
    },
    [
      isNotListed,
      forTable,
      listing,
      updateItemInEvent,
      event?.viagId,
      eventsTransformed,
      updateActivePosEntityInfo,
      posEntity,
      activeAccountWebClientConfig,
      refreshExpandedListItems,
      showErrorDialog,
      lockListingGroupFromEdits,
      hasMemorizeLastBroadcastListingMarketplaceFeature,
    ]
  );

  const { iconComponent: ListingBroadcastIcon, contentId } =
    getListingBroadcastIcon(
      listing.status,
      listing.qcState,
      false,
      true,
      listing.isUndeliv,
      listing.brdcstStatus,
      listing.pendProofState
    );

  if (isPlaceholderEvent) {
    return null;
  }

  if (
    listing.qcState === QualityControlState.Pending &&
    listing.status !== ListingStatus.Listed
  ) {
    return iconOnlyMode ? (
      <IconButton
        icon={
          <PendingQCIcon
            size={vars.iconSize.m}
            stroke={vars.color.textDisabled}
          />
        }
        titleContentId={ContentId.PendingQC}
      />
    ) : null;
  }

  if (isDuplicate) {
    return iconOnlyMode ? (
      <IconButton
        icon={<DuplicateOutlinedIcon size={vars.iconSize.m} />}
        titleContentId={ContentId.DuplicatedListing}
      />
    ) : null;
  }

  return (
    <ButtonWithIcon
      onClick={onBroadcastClick}
      disabled={disabled || isLoading}
      textContentId={
        !iconOnlyMode
          ? isNotListed
            ? ContentId.Broadcast
            : ContentId.Unbroadcast
          : contentId
      }
      icon={
        iconOnlyMode ? (
          <ListingBroadcastIcon
            size={isMobile ? vars.iconSize.s : vars.iconSize.m}
          />
        ) : (
          <SignalIcon size={vars.iconSize.s} />
        )
      }
      variant={variant}
      textColor={variant === 'textPlain' ? 'strong' : 'brand'}
      style={variant === 'textPlain' ? { width: '100%' } : undefined}
      justifyContent="space-between"
      iconOnlyMode={iconOnlyMode}
    />
  );
};
